import { Database } from './db.js';

export const DB_VERSION = 4;
export const STORAGE_KEY = 'dongtianju:save:v1';

/**
 * 建库：所有表结构集中在这里，改结构时同步 DB_VERSION 与迁移脚本。
 *
 * v1 -> v2：扩的是「从城郊到城市」那一整套：
 *   标签表 tags、职业打工、委托任务、证照与额度申请、地块 REALTY、雇工与线程池、
 *   案卷 / 舆论事件 / 违规记录。
 * v2 -> v3：攒车与开车那份家当——vehicles 表。
 */
export function createDatabase() {
  const db = new Database({
    version: DB_VERSION,
    storageKey: STORAGE_KEY,
    migrations: [
      {
        from: 1,
        to: 2,
        up: (obj) => {
          const tables = obj.tables || (obj.tables = {});
          // 新表：给个空壳，load() 之后 Table.fromJSON 会自己认下来
          const empty = { rows: [], seq: 1 };
          for (const name of ['tags', 'jobOffers', 'licenses', 'licenseAudits',
            'questOffers', 'playerQuests', 'shopUnits', 'hired_staff', 'worker_assignments',
            'threads', 'thread_messages', 'cases', 'factionPoints', 'notice_events', 'offenseRecords']) {
            if (!tables[name]) tables[name] = { ...empty };
          }
          // player 补新字段
          const p = tables.player && tables.player.rows ? tables.player.rows[0] : null;
          if (p) {
            p.aura = p.aura == null ? 1 : p.aura;
            p.fatigue = p.fatigue == null ? 0 : p.fatigue;
            p.tiaoanzi = p.tiaoanzi == null ? 0 : p.tiaoanzi;
            p.outfitCleanliness = p.outfitCleanliness == null ? 1 : p.outfitCleanliness;
            p.equippedAccessories = p.equippedAccessories || [];
          }
        },
      },
      {
        from: 2,
        to: 3,
        up: (obj) => {
          const tables = obj.tables || (obj.tables = {});
          if (!tables.vehicles) tables.vehicles = { rows: [], seq: 1 };
        },
      },
      {
        from: 3,
        to: 4,
        up: (obj) => {
          const tables = obj.tables || (obj.tables = {});
          if (!tables.insights) tables.insights = { rows: [], seq: 1 };
        },
      },
    ],
  });

  // 存档元信息
  db.define('meta', { pk: 'key', autoInc: false });

  // 玩家状态（单条，id=1）
  db.define('player', {
    pk: 'id',
    autoInc: false,
    defaults: {
      id: 1, x: 0, y: 0, facing: 'down', scene: 'home',
      satiety: 70, energy: 80, mood: 70, qi: 10,
      tiredness: 0,
      day: 1, minute: 360, playtime: 0,
      cash: 15,
      scenePosStamp: 0,
      exploring: false,   // true = 出门在外（多在城郊时辰照常走）
      aura: 1,            // 「属于这里」的程度：拾荒越少、越体面，值越高
      fatigue: 0,         // 疲劳（0~120）
      tiaoanzi: 0,        // 条半Pref货币（铜钱串）
      outfitCleanliness: 1, // 衣物洁净度：0~1
      equippedAccessories: [], // 佩戴的配饰 id 列表
    },
  });

  // 背包
  db.define('inventory', { pk: 'id', indexes: ['itemId'], defaults: { itemId: '', count: 0 } });

  // 已放置的器物（含翻找之类状态，落在 state 里）
  db.define('props', {
    pk: 'id',
    indexes: ['scene', 'type'],
    defaults: { scene: 'home', type: '', gx: 0, gy: 0, w: 1, h: 1, rot: 0, state: {} },
  });

  // 世界状态（单条，id=1）
  db.define('world', {
    pk: 'id',
    autoInc: false,
    defaults: { id: 1, day: 1, minute: 360, season: 'spring', weather: 'sunny', dayCount: 1 },
  });

  // 日志 / 流水
  db.define('log', { pk: 'id', indexes: ['kind'], defaults: { day: 1, minute: 0, kind: 'info', text: '' } });

  // 剧情 / 教学标记
  db.define('flags', { pk: 'key', autoInc: false, defaults: { value: true } });

  /* ---------------------- 下面都是 v2 的新家当 ---------------------- */

  // 标签与资格证：一条 Tag = 一个来源（工作/装备）的累加值
  db.define('tags', {
    pk: 'key',
    autoInc: false,
    indexes: ['tag'],
    defaults: { key: '', tag: '', src: 'global', value: 0, day: 1, updated: 0 },
  });

  // 打工活儿：{ id, scene, type, needObj:{}, giver, reward_gold, ap_cost, requires_cert, fine_risk, claimCount, created_day, expires_day }
  db.define('jobOffers', { pk: 'id', indexes: ['scene'], defaults: {} });

  // 榜上的委托：{ id, scene, type, target:{}, amount_required, time_limit_minutes, has_deadline_days, reward_gold, spawn_day, dedupeKey, gate_cert }
  db.define('questOffers', { pk: 'id', indexes: ['scene'], defaults: {} });

  // 已接的委托：{ id, offerId, scene, type, target:{}, required, progress, deadline, time_limit_minutes, has_deadline_days, reward_gold, status:'active'|'done'|'failed'|'redeemed' }
  db.define('playerQuests', { pk: 'id', indexes: ['scene', 'status'], defaults: {} });

  // 在手的证照：{ id, needId, status:'active'|'expired'|'revoked', issueDay, expiryDay, max_allowed }
  db.define('licenses', { pk: 'id', indexes: ['needId'], defaults: {} });

  // 办理/续期/加额度的在办记录：{ id, needId, kind, type, stage, stage_started_ms, active }
  db.define('licenseAudits', { pk: 'id', indexes: ['needId'], defaults: {} });

  // 地块：{ id, scene, level, price_seed_offset, monthly_rent_gold, tax_gold, forSale, owned, last_paid_period, for_rent, rent_due_period }
  db.define('shopUnits', { pk: 'id', indexes: ['scene'], defaults: {} });

  // 雇工：{ id, display_name, hire_cost_gold, wage_per_day_gold, assigned_scene, task_id, energy, work_start_ms, gender }
  db.define('hired_staff', { pk: 'id', indexes: ['assigned_scene'], defaults: {} });

  // 派活记录：{ id, worker_id, scene, task_kind, task_id, started_day, finished_day }
  db.define('worker_assignments', { pk: 'id', indexes: ['worker_id'], defaults: {} });

  // 舆论线：{ id, title, thread_type, stage_amount_total, player_max_participation, server_max_participation, days_left, current_stage_index, stage_player_predictions, development_amount_ahead_of_schedule, active }
  db.define('threads', { pk: 'id', defaults: {} });

  // 线程池交互记录: { id, thread_id, kind, role, data }
  db.define('thread_messages', { pk: 'id', indexes: ['thread_id'], defaults: {} });

  // 案卷：{ id, case_type, thread_id, target_a, target_b, status, verdict, evidence:{}, registered_day, days_open }
  db.define('cases', { pk: 'id', indexes: ['thread_id', 'status'], defaults: {} });

  // 势力声望：{ id, group, faith, tone_intensity }
  db.define('factionPoints', { pk: 'id', indexes: ['group'], defaults: {} });

  // 舆论事件: { id, kind, tags:{}, registered_day, data }
  db.define('notice_events', { pk: 'id', defaults: {} });

  // 违规记录：{ id, offender, fine_gold, penalty_kind, registered_day }
  db.define('offenseRecords', { pk: 'id', defaults: {} });

  /* ---------------------- v3：车 ---------------------- */

  /* ---------------------- v4：灵感碎片 ---------------------- */

  // 心里冒出来的念头：{ id, kind:'fragment', key, name, sense, day }
  db.define('insights', {
    pk: 'id',
    indexes: ['kind', 'key'],
    defaults: { kind: 'fragment', key: '', name: '', sense: '', day: 1 },
  });

  // 车：{ id, kind, name, scene, gx, gy, stage, fuel, maxFuel, durability, maxDur, quality, attempts, odo, angle, broken }
  // stage < STAGE_COUNT 时表示还在攒（详见 systems/assemble.js）；攒满即出车，落在某个场景里。
  db.define('vehicles', {
    pk: 'id',
    indexes: ['scene'],
    defaults: {
      kind: 'wreck', name: '攒出来的车', scene: 'carport', gx: 0, gy: 0,
      stage: 0, fuel: 0, maxFuel: 40, durability: 60, maxDur: 60,
      quality: 0, attempts: 0, odo: 0, angle: 0, broken: false,
    },
  });

  return db;
}

/**
 * 新档种子数据。
 * @param {Database} db
 * @param {Object} opts { player:{x,y}, props:[...], inventory:[{itemId,count}] }
 */
export function seedNewGame(db, opts = {}) {
  const now = Date.now();
  const meta = db.table('meta');
  const player = db.table('player');
  const inv = db.table('inventory');
  const props = db.table('props');
  const world = db.table('world');
  const log = db.table('log');
  const flags = db.table('flags');

  meta.clear(); player.clear(); inv.clear(); props.clear(); world.clear(); log.clear(); flags.clear();
  for (const name of ['tags', 'jobOffers', 'licenses', 'licenseAudits', 'questOffers', 'playerQuests',
    'shopUnits', 'hired_staff', 'worker_assignments', 'threads', 'thread_messages', 'cases',
    'factionPoints', 'notice_events', 'offenseRecords', 'insights']) {
    db.table(name).clear();
  }

  meta.insert({ key: 'created_at', value: now });
  meta.insert({ key: 'version', value: DB_VERSION });
  meta.insert({ key: 'playtime', value: 0 });
  meta.insert({ key: 'title', value: '洞天居' });

  player.insert({ ...(opts.player || {}), id: 1, scene: 'home' });

  world.insert({ id: 1, day: 1, minute: 6 * 60, season: 'spring', weather: 'sunny', dayCount: 1 });

  const startItems = opts.inventory || [
    { itemId: 'cash', count: 15 },
    { itemId: 'coin', count: 20 },
    { itemId: 'wood', count: 5 },
    { itemId: 'seed', count: 3 },
    { itemId: 'rice', count: 2 },
  ];
  for (const it of startItems) inv.insert(it);

  for (const p of opts.props || []) props.insert({ scene: 'home', ...p });

  log.insert({ day: 1, minute: 6 * 60, kind: 'story', text: '你推开了洞天居的门。' });
  flags.insert({ key: 'first_enter', value: true });

  db.dirty = true;
  db.save(true);
  return db;
}

/** 常用读写快捷方法（系统层调用，避免到处拼 SQL 式代码） */
export const Repo = {
  getPlayer(db) {
    let p = db.table('player').find(1);
    if (!p) p = db.table('player').insert({ id: 1 });
    return p;
  },
  savePlayer(db, patch) {
    return db.table('player').update(1, patch);
  },
  getWorld(db) {
    let w = db.table('world').find(1);
    if (!w) w = db.table('world').insert({ id: 1 });
    return w;
  },
  saveWorld(db, patch) {
    return db.table('world').update(1, patch);
  },
  addItem(db, itemId, count = 1) {
    const inv = db.table('inventory');
    let row = inv.first((r) => r.itemId === itemId);
    if (row) return inv.update(row.id, { count: row.count + count });
    return inv.insert({ itemId, count });
  },
  countItem(db, itemId) {
    const row = db.table('inventory').first((r) => r.itemId === itemId);
    return row ? row.count : 0;
  },
  takeItem(db, itemId, count = 1) {
    const inv = db.table('inventory');
    const row = inv.first((r) => r.itemId === itemId);
    if (!row || row.count < count) return false;
    const left = row.count - count;
    if (left <= 0) inv.remove(row.id);
    else inv.update(row.id, { count: left });
    return true;
  },
  items(db) {
    return db.table('inventory').all().filter((r) => r.count > 0);
  },
  log(db, text, kind = 'info', day = 1, minute = 0) {
    const t = db.table('log');
    t.insert({ day, minute, kind, text });
    // 只保留最近 200 条
    const rows = t.all();
    if (rows.length > 200) for (let i = 0; i < rows.length - 200; i++) t.remove(rows[i].id);
    return text;
  },
  setFlag(db, key, value = true) { db.table('flags').upsert({ key, value }); },
  getFlag(db, key) { const r = db.table('flags').find(key); return r ? r.value : false; },
  /** 器物所在行（静态场景的器物也都在 props 表里，状态能存住） */
  findPropRow(db, scene, type, gx, gy) {
    return db.table('props').first((r) => r.scene === scene && r.type === type && r.gx === gx && r.gy === gy);
  },
};
